import environment from '../environment';
import { createSuperScriptLinkHtml } from '../components/SuperScriptLink/SuperScriptLink';

export class QuickEstimateCalcConstants {
    static readonly VEHICLE_MILEAGE_INPUT_ERROR = 'Please enter a whole number between 1 and 100 MPG';
    static readonly VEHICLE_DAILY_MILEAGE_INPUT_ERROR = 'Please enter a whole number between 1 and 2000 Miles';
    static readonly VEHICLE_DAILY_RUNTIME_INPUT_ERROR = 'Please enter a whole number between 1 and 24 hours';
    static readonly VEHICLE_STATE_SELECTION = 'Please select a state';
}

export class ColorConstants {
    static readonly BLUE = '#0064D4';
    static readonly YELLOW = '#F1B534';
    static readonly GREY = '#57585D';
    static readonly WHITE = '#FFFFFF';
    static readonly DoughnutColorCodes = ['#1700F4', '#FFC000', '#6A6B71'];
}

export class URLConstants {
    static readonly BOOK_CONSULTATION = '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch';
    static readonly DETAILED_ASSESSMENT = '/e-switch-assist/detailed-assessment';
    static readonly MARKETPLACE = '/en-us/marketplace';
    static readonly QUICK_ASSESSMENT = '/en-us/e-switch-assist/#quick-estimate-calculator';
    static readonly DEFAULT_EMAIL_LINK = 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultations';
}

export class HyperLinkConstants {
    static readonly TITLE_ATTRIBUTE_NAME = 'title';
    static readonly HREF_ATTRIBUTE_NAME = 'href';
    static readonly ANCHOR_TAG_NAME = 'a';
}

export class HTTPHeaderConstants {
    static readonly DEFAULT_COUNTRY_CODE = 'USA';
}

export class DateTimeConstants {
    static readonly EXPORT_FILENAME_DATA_FORMAT = 'MM_DD_YYYY';
}

export class VehicleListTableConstants {
    static readonly BATTERY_COLUMN = 'battery';
    static readonly REGISTRATION_NO_DATA_UNAVAILABLE_STR = '-';
}

export const FilterKeys = {
    BATTERY_FILTER_KEY: 'battery',
    SWITCH_ASSESSMENT_FILTER_KEY: 'switchAssessmentColumnValue',
    SUITABLE_EV_FILTER_KEY: 'suitableEV',
};

export class VehicleListColumnNameHeaderConstants {
    static readonly REGISTRATION = 'REGISTRATION';
    static readonly VIN_NUMBER = 'VIN NUMBER';
    static readonly MODEL_YEAR = 'MODEL/YEAR';
    static readonly SWITCH_ASSESSEMENT = 'SWITCH ASSESSEMENT';
    static readonly SUITABLE_EVS = "SUITABLE EV'S";
    static readonly BATTERY = 'BATTERY';
    static readonly LAST_UPDATED = 'LAST UPDATED';
}

export class VehicleListColumnFieldConstants {
    static readonly REGISTRATION = 'registrationNumber';
    static readonly VIN_NUMBER = 'vin';
    static readonly MODEL_YEAR = 'modelYear';
    static readonly SWITCH_ASSESSEMENT = 'switchAssessmentColumnValue';
    static readonly SUITABLE_EVS = 'suitableEV';
    static readonly BATTERY = 'battery';
    static readonly LAST_UPDATED = 'lastUpdated';
}

export class CountryConstants {
    static readonly USA = 'USA';
    static readonly UK = 'GBR';
    static readonly GER = 'DEU';
    static readonly ES = 'ESP';
    static readonly FR = 'FRA';
    static readonly NO = 'NOR';
    static readonly IT = 'ITA';
    static readonly NL = 'NLD';
    static readonly CA = 'CAN';
}

export class LocalesConstants {
    static readonly USLocale = 'en-us';
    static readonly CALocale = 'en-ca';
    static readonly DELocale = 'de-de';
    static readonly UKLocale = 'en-gb';
}

export class MomentLocalesConstants {
    static readonly USLocale = 'en-us';
    static readonly CALocale = 'en-ca';
    static readonly DELocale = 'de-de';
    static readonly UKLocale = 'en-gb';
    static readonly ITLocale = 'it-it';
    static readonly NOLocale = 'nb-no';
    static readonly NLLocale = 'nl-nl';
    static readonly ESLocale = 'es-es';
    static readonly FRLocale = 'fr-fr';
}

export class BatteryPackConstants {
    static readonly E_TRANSIT_ENHANCED_BATTERY_PACK = '89kWh';
    static readonly E_TRANSIT_CUSTOM_STANDARD_BATTERY_PACK = '64kWh';
    static readonly E_TRANSIT_STANDARD_BATTERY_PACK = '68kWh';
    static readonly F_150_EXTENDED_BATTERY_PACK = '131kWh';
    static readonly F_150_STANDARD_BATTERY_PACK = '98kWh';
}

export interface MarketConfiguration {
    [key: string]: {
        bookConsultationAction: string;
        enableGetStarted: boolean;
        xCountryCode: string;
        batteryCapacity4P: string;
        momentJsLocaleKey: string;
    };
}

export const marketConfiguration: MarketConfiguration = {
    'en-us': {
        bookConsultationAction: environment.FORDPRO_URL + '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch',
        enableGetStarted: true,
        xCountryCode: CountryConstants.USA,
        batteryCapacity4P: '89',
        momentJsLocaleKey: MomentLocalesConstants.USLocale,
    },
    'en-gb': {
        bookConsultationAction: environment.FORDPRO_URL + '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch',
        enableGetStarted: true,
        xCountryCode: CountryConstants.UK,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.UKLocale,
    },
    'de-de': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.GER,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.DELocale,
    },
    'es-es': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.ES,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.ESLocale,
    },
    'fr-fr': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.FR,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.FRLocale,
    },
    'no-no': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.NO,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.NOLocale,
    },
    'it-it': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.IT,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.ITLocale,
    },
    'nl-nl': {
        bookConsultationAction: 'mailto:softwaresolutions@fordpro.com?subject=E-Switch Assist Consultation',
        enableGetStarted: true,
        xCountryCode: CountryConstants.NL,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.NLLocale,
    },
    'en-ca': {
        bookConsultationAction: environment.FORDPRO_URL + '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch',
        enableGetStarted: false,
        xCountryCode: CountryConstants.CA,
        batteryCapacity4P: '68',
        momentJsLocaleKey: MomentLocalesConstants.CALocale,
    },
};

type StringtoStringMap = Record<string, string>;
export const LocaleToConsultationLinksMap: StringtoStringMap = {
    'en-us': environment.FORDPRO_URL + '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch',
    'en-ca': environment.FORDPRO_URL + '/en-us/charging/charging-demo/?type=e-switch&intcmp=fpro-esa-ft-GetInTouch',
    'de-de': URLConstants.DEFAULT_EMAIL_LINK,
    'en-gb': URLConstants.DEFAULT_EMAIL_LINK,
    'es-es': URLConstants.DEFAULT_EMAIL_LINK,
    'fr-fr': URLConstants.DEFAULT_EMAIL_LINK,
    'it-it': URLConstants.DEFAULT_EMAIL_LINK,
    'nl-nl': URLConstants.DEFAULT_EMAIL_LINK,
    'no-no': URLConstants.DEFAULT_EMAIL_LINK,
};

export class DetailAssessmentConstants {
    static readonly DETAIL_ASSESSMENT_PERSONA = 'ESWITCH_PERSONA';
}

export class ExportReportHeaderConstants {
    static readonly COLUMN_HEADER = 'headerColumnName';
}

export class ViewTypeConstants {
    static readonly QUICK_ESTIMATE = 'quick-estimate';
    static readonly DETAILED_ASSESSMENT = 'detailed-assessment';
}

export class AnalyticsConstants {
    static readonly FPG_COOKIES = 'fpg_cookies';
    static readonly COOKIE_PREFERENCES = 'cookiePreferences';
}

export class SymbolConstants {
    static readonly VEHICLE_DISCLOSURE_SYMBOL = '@@777';
    static readonly CHARGER_DISCLOSURE_SYMBOL = '@@673';
}
